import type { Company, Document } from "@/types";
import { computed, type Ref } from "vue";

/**
 * Decides which contracts a signup flow's contract review shows, shared by the EE, ES, LT, LV
 * and PL ContractReviewConfigurationComponents.
 *
 * A contract is included when the flow's current `contractState` is one of the states the
 * contract belongs to. An optional contract (one passed with its contract key) is additionally
 * dropped once the selected company already has a completed document carrying that key, so a
 * producer is never asked to sign it twice.
 */
export function useOptionalContractInclusion(
  selectedCompany: Readonly<Ref<Company | null | undefined>>,
  contractState: Readonly<Ref<number>>
) {
  const hasSignedOptionalContract = (selectedContract: string): boolean => {
    return (
      selectedCompany.value?.documents.some(
        (document: Document) =>
          document.status === "completed" &&
          document.contract_keys.includes(selectedContract)
      ) ?? false
    );
  };

  const shouldSignOptionalContractIfOptional = (
    optionalContractKey: string | null
  ): boolean => {
    return (
      !optionalContractKey || !hasSignedOptionalContract(optionalContractKey)
    );
  };

  const includeDocument = (
    states: number[],
    optionalContractKey: string | null = null
  ) =>
    computed(() => {
      return (
        states.includes(contractState.value) &&
        shouldSignOptionalContractIfOptional(optionalContractKey)
      );
    });

  return { hasSignedOptionalContract, includeDocument };
}
